import { useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Package, Sparkles } from "lucide-react";
import { FloatingHScrollbar } from "@/components/FloatingHScrollbar";
import { useLanguage } from "@/context/LanguageContext";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { ProductThumb } from "@/components/products/ProductThumb";
import { FavoriteStar } from "@/components/products/FavoriteStar";
import { isNewProduct } from "@/lib/productBadges";

// factory cost with its entry currency (USD when entered in USD, otherwise RMB)
const fmtCost = (r) => {
  const cur = r.cost_currency === "USD" || (!r.cost_currency && (r.factory_cost_usd || 0) > 0) ? "USD" : "RMB";
  const val = cur === "USD" ? r.factory_cost_usd : r.factory_cost;
  if (!val) return "-";
  return `${val.toFixed(2)} ${cur}`;
};

export const ProductsTable = ({ products, loading, selected, onToggleSelect, readOnly = false, currency = "USD" }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const priceKey = currency === "CAD" ? "display_price_cad" : "display_price_usd";
  const sym = currency === "CAD" ? "C$" : "US$";
  const boxRef = useRef(null);
  // 2026-06 · 表格高度收到视口底部 → 纵向滚动发生在表格内部，表头吸顶、
  // 左侧「勾选 / 图片 / 产品编号」三列冻结才能一直可见（与采购报价一致）。
  useEffect(() => {
    const calc = () => {
      const inner = boxRef.current?.firstElementChild;
      if (!inner) return;
      const docTop = inner.getBoundingClientRect().top + window.scrollY;
      inner.style.maxHeight = `${Math.max(320, Math.round(window.innerHeight - docTop - 72))}px`;
    };
    calc();
    const tid = setTimeout(calc, 300);
    window.addEventListener("resize", calc);
    return () => { clearTimeout(tid); window.removeEventListener("resize", calc); };
  }, []);

  const HEAD = "sticky top-0 z-20 bg-card";
  const FROZEN = "sticky z-10 bg-card group-hover:bg-muted/50";

  return (
    <>
    <div ref={boxRef} className="bg-card border border-border rounded-md overflow-hidden">
      <Table data-testid="products-table">
        <TableHeader>
          <TableRow>
            <TableHead style={{ width: 36, minWidth: 36, left: 0, zIndex: 30 }} className={HEAD} />
            <TableHead style={{ width: 112, minWidth: 112, left: 36, zIndex: 30 }} className={HEAD}>{t("products.photo")}</TableHead>
            <TableHead style={{ width: 150, minWidth: 150, left: 148, zIndex: 30 }} className={HEAD}>{t("products.itemNumber")}</TableHead>
            <TableHead className={`w-20 ${HEAD}`}>{t("products.tag")}</TableHead>
            <TableHead className={HEAD}>{t("products.description")}</TableHead>
            <TableHead className={HEAD}>{t("priceList.size")}</TableHead>
            <TableHead className={`text-right ${HEAD}`}>{t("priceList.qtyPerCtn")}</TableHead>
            <TableHead className={`text-right ${HEAD}`}>{t("priceList.factoryCost")}</TableHead>
            <TableHead className={`text-right text-brand font-bold ${HEAD}`}>{t("priceList.price")} ({sym})</TableHead>
            {/* 2026-10-22 · 用户要求去掉「40 尺柜装箱量」列 */}
          </TableRow>
        </TableHeader>
        <TableBody>
          {!loading && products.length === 0 && (
            <TableRow>
              <TableCell colSpan={10} className="text-center py-10 text-muted-foreground" data-testid="products-empty-state">
                {t("products.noProducts")}
              </TableCell>
            </TableRow>
          )}
          {products.map((p) => (
            <TableRow
              key={p.item_number}
              data-testid={`product-row-${p.item_number}`}
              onClick={() => navigate(`/products/${encodeURIComponent(p.item_number)}/edit`)}
              className="group cursor-pointer hover:bg-secondary/40"
            >
              <TableCell onClick={(e) => e.stopPropagation()} style={{ width: 36, minWidth: 36, left: 0, zIndex: 11 }} className={`pr-0 ${FROZEN}`}>
                {!readOnly && (
                <Checkbox
                  checked={selected?.has(p.item_number) || false}
                  onCheckedChange={() => onToggleSelect?.(p.item_number)}
                  data-testid={`product-select-${p.item_number}`}
                />
                )}
              </TableCell>
              <TableCell style={{ width: 112, minWidth: 112, left: 36, zIndex: 11 }} className={FROZEN}>
                <ProductThumb
                  photoUrl={p.photo_url}
                  patternPhotoUrl={p.pattern_photo_url}
                  compositePhotoUrl={p.composite_photo_url}
                  alt={p.item_number}
                  size="sm"
                  side="right"
                  className={`${(p.composite_photo_url || (p.pattern_photo_url && p.photo_url)) ? "w-24" : "w-14"} h-12 bg-white rounded-md border border-border`}
                  fallbackIconSize={18}
                />
              </TableCell>
              <TableCell style={{ width: 150, minWidth: 150, left: 148, zIndex: 11 }} className={`font-mono font-semibold text-[13px] ${FROZEN}`} data-testid={`product-item_number-cell-${p.item_number}`}>
                <div className="flex items-center gap-1.5">
                  <span onClick={(e) => e.stopPropagation()} className="inline-flex">
                    <FavoriteStar itemNumber={p.item_number} initial={p.is_favorite} size={14} />
                  </span>
                  {p.item_number}
                  {isNewProduct(p) && (
                    <span
                      className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded-sm bg-amber-400 text-black text-[9px] font-bold uppercase tracking-wide leading-none"
                      data-testid={`product-new-badge-${p.item_number}`}
                    >
                      <Sparkles size={8} /> {t("products.newBadge")}
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell data-testid={`product-tag-cell-${p.item_number}`}>
                {p.tag ? (
                  <span className="inline-block px-1.5 py-0.5 rounded-sm bg-brand/10 text-brand text-[10px] font-medium leading-none max-w-[70px] truncate">
                    {p.tag}
                  </span>
                ) : (
                  <span className="text-muted-foreground text-xs">-</span>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground max-w-[200px] truncate">{p.description}</TableCell>
              {[
                ["size", (r) => r.size || "-", "text-left"],
                ["qty_per_ctn", (r) => r.qty_per_ctn || "-", "text-right"],
                ["factory_cost", fmtCost, "text-right"],
                ["display_price", (r) => (r[priceKey] ? `${sym}${r[priceKey].toFixed(2)}` : "-"), "text-right text-brand"],
                /* 2026-10-22 · 用户要求去掉「40 尺柜装箱量」列（container_qty_40） */
              ].map(([key, fmt, align]) => (
                <TableCell key={key} className={`font-mono text-[11px] whitespace-nowrap ${align}${key === "factory_cost" || key === "display_price" ? " font-bold" : ""}`} data-testid={`product-${key}-cell-${p.item_number}`}>
                  {(p.price_lists || []).length === 0
                    ? "-"
                    : p.price_lists.slice(0, 4).map((r, ri) => (
                        <div key={r.id || ri} className="leading-5">{fmt(r)}</div>
                      ))}
                  {(p.price_lists || []).length > 4 && <div className="text-muted-foreground">+{p.price_lists.length - 4}</div>}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
    <FloatingHScrollbar targetRef={boxRef} />
    </>
  );
};
